import React from 'react'
import { cn } from '../utils'
import { Progress } from '../progress/Progress'
import { Badge } from '../badge/Badge'
import { Spinner } from '../spinner/Spinner'
import { CheckCircle, XCircle, AlertCircle, Clock } from 'lucide-react'

export interface JobProgressProps {
  status: 'pending' | 'processing' | 'completed' | 'failed'
  progress: number
  error?: string
  title?: string
  subtitle?: string
  className?: string
  showIcon?: boolean
  showBadge?: boolean
  compact?: boolean
}

const statusConfig = {
  pending: {
    icon: Clock,
    color: 'text-gray-500',
    bgColor: 'bg-gray-100',
    badgeVariant: 'secondary' as const,
    label: 'Pending',
  },
  processing: {
    icon: Spinner,
    color: 'text-blue-500',
    bgColor: 'bg-blue-100',
    badgeVariant: 'default' as const,
    label: 'Processing',
  },
  completed: {
    icon: CheckCircle,
    color: 'text-green-500',
    bgColor: 'bg-green-100',
    badgeVariant: 'success' as const,
    label: 'Completed',
  },
  failed: {
    icon: XCircle,
    color: 'text-red-500',
    bgColor: 'bg-red-100',
    badgeVariant: 'destructive' as const,
    label: 'Failed',
  },
}

export const JobProgress: React.FC<JobProgressProps> = ({
  status,
  progress,
  error,
  title,
  subtitle,
  className,
  showIcon = true,
  showBadge = true,
  compact = false,
}) => {
  const config = statusConfig[status]
  const Icon = config.icon

  if (compact) {
    return (
      <div className={cn('flex items-center gap-2', className)}>
        {showIcon && (
          <div className={cn('flex-shrink-0', config.color)}>
            {status === 'processing' ? (
              <Spinner className="h-4 w-4" />
            ) : (
              <Icon className="h-4 w-4" />
            )}
          </div>
        )}
        {status === 'processing' && (
          <div className="flex-1 max-w-[100px]">
            <Progress value={progress} className="h-2" />
          </div>
        )}
        {showBadge && <Badge variant={config.badgeVariant}>{config.label}</Badge>}
      </div>
    )
  }

  return (
    <div className={cn('space-y-3', className)}>
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-3">
          {showIcon && (
            <div className={cn('mt-0.5 flex-shrink-0', config.color)}>
              {status === 'processing' ? (
                <Spinner className="h-5 w-5" />
              ) : (
                <Icon className="h-5 w-5" />
              )}
            </div>
          )}
          <div className="space-y-1">
            {title && <h4 className="text-sm font-medium leading-none">{title}</h4>}
            {subtitle && <p className="text-sm text-muted-foreground">{subtitle}</p>}
          </div>
        </div>
        {showBadge && <Badge variant={config.badgeVariant}>{config.label}</Badge>}
      </div>

      {status === 'processing' && (
        <div className="space-y-1">
          <Progress value={progress} className="h-2" />
          <p className="text-xs text-muted-foreground">{progress}% complete</p>
        </div>
      )}

      {status === 'failed' && error && (
        <div className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 p-3">
          <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0 text-red-500" />
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}
    </div>
  )
}